import React, { useState, useRef, memo, useEffect, useCallback } from 'react';
import {
  deleteToastNotification,
  updateDevicesPageViewDevicesListModal,
  doDeviceGroupUpdate,
  doDeviceGroupUndoDeletion,
  doUpdateAllUserDeviceGroups,
  addToastNotification
} from 'redux/actions';
import { recoverUserPreference } from 'redux/UserPreferences';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { IconLoader } from 'common/IconLoader';
import { logger } from 'common/utils/helpers';
import { v4 } from 'uuid';
import ToastNotificationActionTypes from './constants';
import './ToastNotifications.css';
import { selectDeviceGroups } from 'redux/selectors';

function undoRemoveDevice({ groupName, deletedDeviceId, onSuccess, onFailure }) {
  return (dispatch, getState) => {
    const state = getState();
    const deviceGroups = selectDeviceGroups(state);
    const deviceGroup = deviceGroups[groupName];
    const { name, devices, userId, deviceGroupId } = deviceGroup;
    const newDeviceIds = [...devices, ...deletedDeviceId];

    dispatch(doDeviceGroupUpdate(deviceGroupId, name, deviceGroup.default, newDeviceIds, userId, onSuccess, onFailure));
  };
}

function setDefaultDeviceGroup({ deletedDeviceGroup }) {
  return (dispatch, getState) => {
    const state = getState();
    const deviceGroups = selectDeviceGroups(state);
    let newDeletedDeviceGroup = JSON.parse(JSON.stringify(deletedDeviceGroup));
    let { name, userId, devices, deviceGroupId } = newDeletedDeviceGroup;
    let hasNewDefault = false;
    Object.keys(deviceGroups).forEach((name) => {
      let deviceGroup = deviceGroups[name];
      if (deviceGroup.default === true && deviceGroup.name !== newDeletedDeviceGroup.name) {
        newDeletedDeviceGroup.default = false;
        hasNewDefault = true;
      }
    });
    let newDeviceGroups = { ...deviceGroups, [newDeletedDeviceGroup.name]: newDeletedDeviceGroup };

    if (hasNewDefault) {
      dispatch(doDeviceGroupUpdate(deviceGroupId, name, newDeletedDeviceGroup.default, devices, userId));
    }

    dispatch(doUpdateAllUserDeviceGroups(newDeviceGroups));
  };
}

const ToastNotification = (props) => {
  const intervalRef = useRef(null);
  const [exit, setExit] = useState(false);
  const [progress, setProgress] = useState(0);

  const handlePauseTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const handleStartTimer = useCallback(() => {
    handlePauseTimer();

    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev < 100) {
          return prev + 0.5;
        }

        handlePauseTimer();
        return prev;
      });
    }, 20);
  }, [handlePauseTimer]);

  const handleCloseNotification = () => {
    setExit(true);
    props.dispatch && props.dispatch(deleteToastNotification(props.id));
  };

  const handleAutoCloseNotification = useCallback(() => {
    handlePauseTimer();
    setExit(true);
    setTimeout(() => {
      props.dispatch && props.dispatch(deleteToastNotification(props.id));
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dispatch, props.id]);

  const handleAction = () => {
    let { deletedDeviceId, groupName } = props;
    let cb_success = null;
    let cb_failed = null;
    if (!props.dispatch) return;
    switch (props.actionType) {
      case ToastNotificationActionTypes.VIEW_DEVICE_LIST:
        props.dispatch(updateDevicesPageViewDevicesListModal(true));
        handleCloseNotification();
        break;
      case ToastNotificationActionTypes.UNDO_REMOVED_DEVICE:
        props.callback && props.callback(true);
        props.dispatch(
          undoRemoveDevice({
            groupName,
            deletedDeviceId,
            onSuccess: () => {
              props.callback && props.callback(false);
            },
            onFailure: () => {
              props.dispatch(
                addToastNotification({
                  id: v4(),
                  title: groupName,
                  description: 'Failed to remove ',
                  deletedDeviceId,
                  groupName,
                  reverse: true
                })
              );
            }
          })
        );

        handleCloseNotification();
        break;
      case ToastNotificationActionTypes.UNDO_REMOVED_DEVICE_LIST:
        props.callback && props.callback(true);
        cb_success = () => {
          props.callback && props.callback(false);
          props.dispatch(
            setDefaultDeviceGroup({
              deletedDeviceGroup: props.deletedDeviceGroup
            })
          );
        };

        cb_failed = () => {
          props.callback && props.callback(false);
          props.dispatch(
            addToastNotification({
              id: v4(),
              title: groupName,
              description: 'Failed to undo deletion of ',
              deletedDeviceId,
              groupName,
              reverse: true
            })
          );
        };

        props.dispatch(doDeviceGroupUndoDeletion(props.deletedDeviceGroup.deviceGroupId, cb_success, cb_failed));
        handleCloseNotification();
        break;
      case ToastNotificationActionTypes.UNDO_REMOVED_FILTER_SET:
      case ToastNotificationActionTypes.UNDO_REMOVED_CHART_SET:
        props.callback && props.callback(true);

        props
          .dispatch(
            recoverUserPreference({
              preferenceId: props.preferenceId,
              category: props.preferenceCategory
            })
          )
          .then(() => {
            props.dispatch(
              addToastNotification({
                id: v4(),
                title: props.title,
                description: 'was successfully recovered ',
                groupName: props.title
              })
            );
          })
          .catch((error) => {
            logger.error(error);

            props.dispatch(
              addToastNotification({
                id: v4(),
                title: props.title,
                description: 'Failed to undo deletion of ',
                groupName: props.title,
                reverse: true
              })
            );
          })
          .finally(() => {
            props.callback && props.callback(false);
          });

        handleCloseNotification();
        break;
      case ToastNotificationActionTypes.VIEW_SW_UPDATE_PROGRESS:
        props.callback && props.callback(true);
        handleCloseNotification();
        break;
      default:
        return;
    }
  };

  useEffect(() => {
    if (progress === 100) {
      // Close notification
      handleAutoCloseNotification();
    }
  }, [progress, handleAutoCloseNotification]);

  useEffect(() => {
    handleStartTimer();

    return handlePauseTimer;
  }, [handlePauseTimer, handleStartTimer]);

  return (
    <div
      role="alert"
      onMouseEnter={handlePauseTimer}
      onMouseLeave={handleStartTimer}
      className={`Toast_Notification_Item ${exit ? 'exit' : ''}`}
    >
      <div id="ActiveToast" className="Toast_Notification_Item_Message_Container">
        <div className="Toast_Notification_Box">
          {!props.reverse && <div className="Toast_Notification_Title">{props.title}</div>}
          <div className="Toast_Notification_Description" title={props.description}>
            {props.description}
          </div>
          {props.reverse && <div className="Toast_Notification_Title">{props.title}</div>}
          {props.actionMessage && (
            <div className="Toast_Notification_ActionMessage" onClick={() => handleAction()}>
              {props.actionMessage}
            </div>
          )}
        </div>
        <div className="Toast_Notification_Close_Button">
          <SvgAF
            img={IconLoader.icon_close_white}
            hover={IconLoader.icon_close_white_hover}
            onClick={() => handleCloseNotification()}
          />
        </div>
      </div>
    </div>
  );
};

export default memo(ToastNotification);
